import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ContentGrid, Typography } from "@murm/ant";
import SearchBar from "../../components/search/SearchBar";
import { DataCatalogModal } from "../../components/dataCatalog/DataCatalog";
import { useDataCatalogSearch } from "../../hooks/useDataCatalogSearch";
import { useIsStacked } from "../../hooks/useIsStacked";
import { cx } from "../../lib/classNames";
import { useAuth } from "../../hooks/useAuth";
import { useAtlasDataVersion, formatAtlasDataVersionLabel } from "../../hooks/useAtlasDataVersion";
import {
  clearError,
  setQuery,
  setKnownParse,
  setPendingSegments,
  setLastSearchSegments,
} from "../../store/searchPage";
import { searchPlaceholderSegments } from "./templates";
import {
  getRecentSearches,
  getSearchCount,
  renameSearch,
  deleteSearch,
} from "../../lib/recentSearches";
import { parametersToSegments } from "../../lib/search/parametersToSegments";
import { segmentsContainRealPill } from "../../lib/search/segmentsContainRealPill";
import { withoutToplinePill } from "../../lib/search/topline";
import TemplateSuggestions from "./components/TemplateSuggestions";
import SavedSearchesPanel from "./components/SavedSearchesPanel";
import { trackEvent } from "../../lib/analytics";
import { PENDO_ID, PENDO_EVENT, pendoId } from "../../lib/pendoIds";
import styles from "./LandingPage.module.css";

const { Title, Text, Link } = Typography;

const LandingPage = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const atlasDataVersionLabel = formatAtlasDataVersionLabel(useAtlasDataVersion());
  const isStacked = useIsStacked();
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const { applySelection } = useDataCatalogSearch();
  // Template hover-preview is a preview layer only, never touching the search bar's committed
  // segments -- hovering just swaps what the richPlaceholderSegments overlay shows; only a click
  // commits.
  const [hoveredTemplateSegments, setHoveredTemplateSegments] = useState(null);

  // localStorage-backed recent searches (lib/recentSearches.js, written by SearchResults.js's
  // Save Search button) -- per-browser, not per-account, since there's no saved-searches API yet.
  //
  // Rename/Delete write straight to localStorage, so nothing re-renders this component
  // afterward -- bumping this forces a re-render, which re-reads getRecentSearches() fresh. The
  // value itself is never read.
  const [, refreshSavedSearches] = useState(0);
  const savedSearches = getRecentSearches().map((entry) => ({
    id: entry.searchedAt,
    label: entry.label,
    query: entry.query,
    parameters: entry.parameters,
    originalSegments: entry.originalSegments,
  }));
  // Two independent thresholds: a user with 2 executed searches sees "Welcome back" but still
  // the longer "Not sure where to start?" template copy. Saved Searches' own empty state is
  // driven separately, by savedSearches.length.
  const searchCount = getSearchCount();
  const hasExecutedFirstSearch = searchCount >= 1;
  const hasExecutedFiveSearches = searchCount >= 5;
  const firstName = user?.name?.split(" ")[0];

  useEffect(() => {
    document.body.classList.add("bg-style-bg");
    dispatch(clearError());
    return () => {
      document.body.classList.remove("bg-style-bg");
    };
  }, [dispatch]);

  // Fills the search bar with the template's pills as real editable tokens, not a flattened
  // string -- dispatched as a one-shot `pendingSegments` message SearchBar/TokenizedSearchInput
  // resolves against the live term index.
  const handleSelectTemplate = (template) => {
    trackEvent(PENDO_EVENT.SEARCH_TEMPLATE_APPLIED, { template: template.query });
    // Otherwise resurfaces as a stale fallback once this pendingSegments seed is consumed.
    dispatch(setLastSearchSegments(null));
    dispatch(setPendingSegments(template.segments));
  };

  // Fallback chain, most faithful first: `originalSegments` (exactly what was typed/clicked) when
  // it has a real pill; else `parameters` (the backend's parse); else plain query text.
  // `segmentsContainRealPill` matches SearchResults.js's own check, so a search doesn't flip
  // between tokenized/translated forms depending on which screen reapplies it.
  const handleSelectSavedSearch = ({ query, parameters, originalSegments }) => {
    dispatch(setLastSearchSegments(null)); // see handleSelectTemplate's own comment
    // The saved parse runs again as is, so picking a saved search never costs an LLM parse.
    if (parameters && query) {
      dispatch(setKnownParse({ query, parameters }));
    }
    const restoredSegments = withoutToplinePill(originalSegments);
    if (segmentsContainRealPill(restoredSegments)) {
      dispatch(setPendingSegments(restoredSegments));
    } else if (parameters) {
      dispatch(setPendingSegments(parametersToSegments(parameters)));
    } else {
      dispatch(setQuery(query));
    }
  };

  const handleRenameSavedSearch = (query, newLabel) => {
    renameSearch(query, newLabel);
    refreshSavedSearches((count) => count + 1);
  };

  const handleDeleteSavedSearch = (query) => {
    deleteSearch(query);
    refreshSavedSearches((count) => count + 1);
  };

  return (
    <div className={cx(styles.page, isStacked && styles.pageStacked)}>
      <ContentGrid className={cx(styles.grid, isStacked && styles.gridStacked)}>
        <div className={isStacked ? styles.columnsStacked : styles.columns}>
          <div className={cx(styles.main, isStacked && styles.mainStacked)}>
            <Title level={2} className={styles.title}>
              {hasExecutedFirstSearch
                ? `Welcome back, ${firstName || "there"}.`
                : "Welcome, let’s find your first insight."}
            </Title>

            <div className={styles.search}>
              <SearchBar
                useCarousel={false}
                staticPlaceholder="Search"
                richPlaceholderSegments={hoveredTemplateSegments ?? searchPlaceholderSegments}
              />
              <Text className={styles.catalogLink}>
                or{" "}
                <Link
                  {...pendoId(PENDO_ID.SEARCH_CATALOG_LINK)}
                  onClick={() => setIsCatalogOpen(true)}
                >
                  browse the data catalog
                </Link>
              </Text>
            </div>

            <div className={styles.templates}>
              <TemplateSuggestions
                onSelectTemplate={handleSelectTemplate}
                onHoverTemplate={(template) => setHoveredTemplateSegments(template.segments)}
                onHoverEnd={() => setHoveredTemplateSegments(null)}
                heading={hasExecutedFiveSearches ? "Templates" : undefined}
                description={
                  hasExecutedFiveSearches
                    ? "Select a template and modify the data to make it your own."
                    : undefined
                }
              />
            </div>

            {atlasDataVersionLabel ? (
              <Text
                type="secondary"
                className={cx(styles.dataVersion, isStacked && styles.dataVersionStacked)}
              >
                {atlasDataVersionLabel}
              </Text>
            ) : null}
          </div>

          <div className={cx(styles.rail, isStacked && styles.railStacked)}>
            <SavedSearchesPanel
              savedSearches={savedSearches}
              onSelectSearch={handleSelectSavedSearch}
              onRename={handleRenameSavedSearch}
              onDelete={handleDeleteSavedSearch}
              bordered={!isStacked}
            />
          </div>
        </div>
      </ContentGrid>

      <DataCatalogModal
        isOpen={isCatalogOpen}
        onClose={() => setIsCatalogOpen(false)}
        onApplySelection={applySelection}
      />
    </div>
  );
};

export { LandingPage };
